import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { SiteLayout } from "@/components/SiteLayout";
import { getUseCase } from "@/lib/cases-content";

export const Route = createFileRoute("/casos-de-uso/$slug")({
  loader: ({ params }) => {
    const useCase = getUseCase(params.slug);
    if (!useCase) throw notFound();
    return { useCase };
  },
  head: ({ params, loaderData }) => {
    const url = `https://www.trajetofacil.com.br/casos-de-uso/${params.slug}`;
    if (!loaderData) {
      return {
        meta: [{ title: "Caso de uso não encontrado — TrajetoFácil" }, { name: "robots", content: "noindex" }],
      };
    }
    const { useCase } = loaderData;
    return {
      meta: [
        { title: `${useCase.title} — TrajetoFácil` },
        { name: "description", content: useCase.description },
        { property: "og:title", content: useCase.title },
        { property: "og:description", content: useCase.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: useCase.title,
            description: useCase.description,
            dateModified: useCase.updatedAt,
            inLanguage: "pt-BR",
            mainEntityOfPage: url,
            publisher: {
              "@type": "Organization",
              name: "TrajetoFácil",
              url: "https://www.trajetofacil.com.br",
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: UseCaseNotFound,
  component: UseCasePage,
});

function UseCaseNotFound() {
  return (
    <SiteLayout>
      <h1 className="text-2xl font-extrabold tracking-tight text-foreground">Caso de uso não encontrado</h1>
      <p className="mt-3 text-muted-foreground">Esta página não existe ou foi movida.</p>
      <Link to="/casos-de-uso" className="mt-4 inline-flex items-center gap-1 font-medium text-primary">
        <ArrowLeft className="size-4" /> Ver todos os casos de uso
      </Link>
    </SiteLayout>
  );
}

function UseCasePage() {
  const { useCase } = Route.useLoaderData();

  return (
    <SiteLayout>
      <nav className="mb-6 text-sm text-muted-foreground">
        <Link to="/casos-de-uso" className="transition hover:text-foreground">Casos de uso</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{useCase.title}</span>
      </nav>

      <article className="space-y-8 text-base leading-7 text-foreground">
        <header>
          <h1 className="text-3xl font-extrabold tracking-tight">{useCase.title}</h1>
          <p className="mt-3 text-lg leading-8 text-muted-foreground">{useCase.intro}</p>
        </header>

        {useCase.sections.map((section) => (
          <section key={section.heading} className="space-y-3">
            <h2 className="text-xl font-bold">{section.heading}</h2>
            {section.paragraphs.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
            {section.list ? (
              <ul className="list-disc space-y-1.5 pl-6">
                {section.list.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}

        <aside className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
          <h2 className="text-lg font-bold">Faça a sua simulação</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Informe origem, destino e os dados do veículo na calculadora gratuita e receba distância,
            tempo, combustível e pedágio — ida e volta, com o custo total da viagem.
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition hover:opacity-90"
          >
            Abrir a calculadora <ArrowRight className="size-4" />
          </Link>
        </aside>

        <p className="text-sm text-muted-foreground">
          Veja também a{" "}
          <Link to="/metodologia" className="font-medium text-primary underline underline-offset-4">
            metodologia de cálculo
          </Link>{" "}
          e os{" "}
          <Link to="/guias" className="font-medium text-primary underline underline-offset-4">
            guias com exemplos
          </Link>
          .
        </p>
      </article>
    </SiteLayout>
  );
}
